/**
 * Equipment & Safety Legal References
 *
 * Key paragraphs from StVZO, BetrSichV, and ArbSchG
 * relevant for farm equipment and workplace safety.
 */

import type { LegalReference } from '@hofsync/design-system';

/**
 * StVZO § 29 - Hauptuntersuchung (TÜV)
 */
export const STVZO_29: LegalReference = {
  id: 'stvzo-29',
  law: 'StVZO',
  lawFullName: 'Straßenverkehrs-Zulassungs-Ordnung',
  paragraph: '§ 29',
  title: 'Untersuchung der Kraftfahrzeuge und Anhänger',
  shortCitation: 'StVZO § 29',
  quote:
    '(1) Die Halter von zulassungspflichtigen Fahrzeugen haben ihre Fahrzeuge auf ihre ' +
    'Kosten nach Maßgabe der Anlage VIII in Verbindung mit Anlage VIIIa in regelmäßigen ' +
    'Zeitabständen untersuchen zu lassen. Für land- oder forstwirtschaftliche ' +
    'Zugmaschinen und Anhänger beträgt die Frist zur Hauptuntersuchung 24 Monate.',
  effectiveDate: '26. April 2012',
  lastAmended: '12. November 2021',
  sourceUrl: 'https://www.gesetze-im-internet.de/stvzo_2012/__29.html',
  relevanceExplanation:
    'Land- und forstwirtschaftliche Fahrzeuge müssen alle 2 Jahre zur Hauptuntersuchung.',
};

/**
 * BetrSichV § 3 - Gefährdungsbeurteilung
 */
export const BETRSICHV_3: LegalReference = {
  id: 'betrsichv-3',
  law: 'BetrSichV',
  lawFullName: 'Betriebssicherheitsverordnung',
  paragraph: '§ 3',
  title: 'Gefährdungsbeurteilung',
  shortCitation: 'BetrSichV § 3',
  quote:
    '(1) Der Arbeitgeber hat vor der Verwendung von Arbeitsmitteln die auftretenden ' +
    'Gefährdungen zu beurteilen (Gefährdungsbeurteilung) und daraus notwendige und ' +
    'geeignete Schutzmaßnahmen abzuleiten. Das Ergebnis der Gefährdungsbeurteilung ist ' +
    'vor der erstmaligen Verwendung der Arbeitsmittel zu dokumentieren.',
  effectiveDate: '3. Februar 2015',
  lastAmended: '21. Juli 2021',
  sourceUrl: 'https://www.gesetze-im-internet.de/betrsichv_2015/__3.html',
  relevanceExplanation:
    'Für alle Arbeitsmittel muss eine Gefährdungsbeurteilung vorliegen.',
};

/**
 * BetrSichV § 14 - Prüfung von Arbeitsmitteln
 */
export const BETRSICHV_14: LegalReference = {
  id: 'betrsichv-14',
  law: 'BetrSichV',
  lawFullName: 'Betriebssicherheitsverordnung',
  paragraph: '§ 14',
  title: 'Prüfung von Arbeitsmitteln',
  shortCitation: 'BetrSichV § 14',
  quote:
    '(1) Der Arbeitgeber hat Arbeitsmittel, deren Sicherheit von den Montagebedingungen ' +
    'abhängt, vor der erstmaligen Verwendung von einer zur Prüfung befähigten Person ' +
    'prüfen zu lassen. (2) Arbeitsmittel, die Schäden verursachenden Einflüssen ' +
    'ausgesetzt sind, hat der Arbeitgeber wiederkehrend von einer zur Prüfung befähigten ' +
    'Person prüfen zu lassen.',
  effectiveDate: '3. Februar 2015',
  lastAmended: '21. Juli 2021',
  sourceUrl: 'https://www.gesetze-im-internet.de/betrsichv_2015/__14.html',
  relevanceExplanation:
    'Arbeitsmittel wie Güllefässer, Anhänger und Maschinen müssen regelmäßig geprüft werden.',
};

/**
 * EnergieStG § 57 - Agrardiesel-Steuerentlastung
 */
export const ENERGIESTG_57: LegalReference = {
  id: 'energiestg-57',
  law: 'EnergieStG',
  lawFullName: 'Energiesteuergesetz',
  paragraph: '§ 57',
  title: 'Steuerentlastung für Betriebe der Land- und Forstwirtschaft',
  shortCitation: 'EnergieStG § 57',
  quote:
    '(1) Eine Steuerentlastung wird auf Antrag gewährt für nachweislich nach § 2 Abs. 1 Nr. 4 ' +
    'versteuerten Gasöle (Dieselkraftstoff), die in Betrieben der Land- und Forstwirtschaft ' +
    'verwendet worden sind. (2) Der Antrag ist nach Ablauf des Kalenderjahres, in dem das ' +
    'Gasöl verwendet worden ist, für diesen Zeitraum bei dem für den Antragsteller zuständigen ' +
    'Hauptzollamt zu stellen.',
  effectiveDate: '15. Juli 2006',
  lastAmended: '24. Oktober 2022',
  sourceUrl: 'https://www.gesetze-im-internet.de/energiestg/__57.html',
  relevanceExplanation:
    'Landwirte können für Agrardiesel eine Steuerentlastung (ca. 21,48 ct/L) beim Hauptzollamt beantragen. ' +
    'Der Antrag muss bis 30. September des Folgejahres gestellt werden.',
};

/**
 * ArbSchG § 12 - Unterweisung
 */
export const ARBSCHG_12: LegalReference = {
  id: 'arbschg-12',
  law: 'ArbSchG',
  lawFullName: 'Arbeitsschutzgesetz',
  paragraph: '§ 12',
  title: 'Unterweisung',
  shortCitation: 'ArbSchG § 12',
  quote:
    '(1) Der Arbeitgeber hat die Beschäftigten über Sicherheit und Gesundheitsschutz ' +
    'bei der Arbeit während ihrer Arbeitszeit ausreichend und angemessen zu unterweisen. ' +
    'Die Unterweisung umfasst Anweisungen und Erläuterungen, die eigens auf den ' +
    'Arbeitsplatz oder den Aufgabenbereich der Beschäftigten ausgerichtet sind. ' +
    'Die Unterweisung muss bei der Einstellung, bei Veränderungen im Aufgabenbereich, ' +
    'der Einführung neuer Arbeitsmittel oder einer neuen Technologie vor Aufnahme der ' +
    'Tätigkeit der Beschäftigten erfolgen. Die Unterweisung muss an die Gefährdungsentwicklung ' +
    'angepasst sein und erforderlichenfalls regelmäßig wiederholt werden.',
  effectiveDate: '7. August 1996',
  lastAmended: '22. Dezember 2020',
  sourceUrl: 'https://www.gesetze-im-internet.de/arbschg/__12.html',
  relevanceExplanation:
    'Mitarbeiter müssen regelmäßig (mindestens jährlich) zu Arbeitssicherheit unterwiesen werden.',
};

/**
 * All Equipment & Safety legal references indexed by ID
 */
export const EQUIPMENT_SAFETY_LEGAL_REFS: Record<string, LegalReference> = {
  'stvzo-29': STVZO_29,
  'betrsichv-3': BETRSICHV_3,
  'betrsichv-14': BETRSICHV_14,
  'arbschg-12': ARBSCHG_12,
  'energiestg-57': ENERGIESTG_57,
};
